import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Receipt, Check, MessageCircle, IndianRupee, Wallet } from 'lucide-react';
import {
  apiClient,
  restaurantOrderService,
  salesInvoiceService,
  type RestaurantOrder,
  type RestaurantOrderStatus,
} from '@kss/api';
import type { DineRootState } from './dineSlice';
import {
  findInvoiceIdInRemarks,
  issueBillForOrders,
  markInvoicePaid,
  whatsappInvoiceShareUrl,
} from './billingHelpers';

interface BillLine {
  name: string;
  quantity: number;
  unitPrice: number;
  taxRate?: number;
}

interface BillSummary {
  invoiceId: string;
  invoiceNumber?: string;
  subtotal: number;
  taxAmount: number;
  total: number;
  lines: BillLine[];
  status: 'Pending' | 'Paid' | string;
}

/**
 * Customer Pay-Bill tab.
 *
 * The flow:
 *   1. Polls the customer's placed orders to find any with status
 *      `Delivered`. Once every order is Delivered (or already Billed/
 *      Paid), enables the "Request Bill" CTA.
 *   2. Tapping "Request Bill" calls `issueBillForOrders()` which
 *      creates a SalesInvoice from the customer's orders and stamps
 *      them with `status='Billed'` + invoice id.
 *   3. Bill renders with itemised lines + Pay button.
 *   4. Pay opens a UPI / Cash chooser → `markInvoicePaid()` records
 *      a SalesPayment and flips invoice + orders to `Paid`.
 *   5. On success → green "Thanks!" panel + WhatsApp share button.
 */
export default function DinePayBill() {
  const { tenantCode = '', qrToken = '' } = useParams<{ tenantCode: string; qrToken: string }>();
  const table     = useSelector((s: DineRootState) => s.dine.table);
  const placedIds = useSelector((s: DineRootState) => s.dine.placedOrderIds);
  const customer  = useSelector((s: DineRootState) => s.dine.customer);

  const [orderStatuses, setOrderStatuses] = useState<RestaurantOrderStatus[]>([]);
  const [bill, setBill] = useState<BillSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<'request' | 'pay' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);

  /** Walk the customer's orders → look for a Billed/Paid one with invoiceId. */
  const refresh = useCallback(async () => {
    if (placedIds.length === 0) {
      setOrderStatuses([]);
      setBill(null);
      return;
    }
    setLoading(true);
    try {
      // Primary: dine-anonymous /orders (§41 / G54). 401 today, will
      // work the moment backend drops [Authorize].
      let valid: RestaurantOrder[] = [];
      try {
        const r = await apiClient.get<{ orders?: RestaurantOrder[] } | RestaurantOrder[]>(
          `/restaurant/dine/${encodeURIComponent(qrToken)}/orders`,
        );
        const rows = Array.isArray(r.data) ? r.data : (r.data?.orders ?? []);
        valid = rows.filter((row): row is RestaurantOrder => !!row && placedIds.includes(String(row.id ?? '')));
      } catch {
        // Fallback: per-id fan-out via legacy auth-required endpoint.
        const rows = await Promise.all(
          placedIds.map((id) => restaurantOrderService.get(id).catch(() => null)),
        );
        valid = rows.filter((r): r is NonNullable<typeof r> => !!r);
      }
      setOrderStatuses(valid.map((r) => (r.status ?? 'Placed') as RestaurantOrderStatus));

      const invoiceId =
        valid.map((r) => findInvoiceIdInRemarks(r.remarks ?? undefined)).find(Boolean) ?? null;
      if (!invoiceId) {
        setBill(null);
        return;
      }
      // Fetch invoice details
      try {
        const inv = await salesInvoiceService.get(invoiceId);
        let payload: { lines?: BillLine[]; subtotal?: number; taxAmount?: number; total?: number } = {};
        try { if (inv.description?.startsWith('{')) payload = JSON.parse(inv.description); } catch { /* */ }
        setBill({
          invoiceId,
          invoiceNumber: inv.code ?? undefined,
          subtotal: payload.subtotal ?? 0,
          taxAmount: payload.taxAmount ?? 0,
          total: payload.total ?? payload.subtotal ?? 0,
          lines: payload.lines ?? [],
          status: (inv.status as string) ?? 'Pending',
        });
        if (inv.status === 'Paid') setPaid(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Could not load the invoice.');
      }
    } finally {
      setLoading(false);
    }
  }, [placedIds.join(','), qrToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void refresh();
    if (placedIds.length === 0) return;
    // Refresh every 15 s — picks up panel-side state changes.
    const t = window.setInterval(() => void refresh(), 15_000);
    return () => window.clearInterval(t);
  }, [refresh, placedIds.length]);

  const allDelivered = orderStatuses.length > 0 && orderStatuses.every((s) =>
    s === 'Delivered' || s === 'Served' || s === 'Billed' || s === 'Paid',
  );
  const canRequest = allDelivered && !bill;

  async function requestBill() {
    setBusy('request');
    setErr(null);
    try {
      await issueBillForOrders(placedIds, table);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not generate the bill.');
    } finally {
      setBusy(null);
    }
  }

  async function pay(method: 'UPI' | 'Cash') {
    if (!bill) return;
    setBusy('pay');
    setErr(null);
    try {
      await markInvoicePaid(bill.invoiceId, bill.total, method, placedIds);
      setPaid(true);
      setShowPayModal(false);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Payment recording failed.');
    } finally {
      setBusy(null);
    }
  }

  /* --- Renders ---------------------------------------------------- */

  // EMPTY: customer has not ordered yet
  if (placedIds.length === 0) {
    return (
      <Empty
        title="No Bill Generated Yet"
        subtitle="Your bill will appear here once you place your order."
        ctaTo={`/dine/${tenantCode}/t/${qrToken}/menu`}
      />
    );
  }

  // ORDERED but not delivered
  if (!bill && !canRequest) {
    return (
      <div className="px-4 py-6 text-center text-sm text-content-muted">
        <Receipt size={28} className="mx-auto mb-3 text-content-muted" />
        Your order is being prepared. The bill will be available once everything is delivered.
        <button
          type="button"
          onClick={() => void refresh()}
          className="ml-2 underline text-primary"
          disabled={loading}
        >
          {loading ? 'checking…' : 'Refresh'}
        </button>
      </div>
    );
  }

  // DELIVERED but no bill yet → Request Bill button
  if (!bill && canRequest) {
    return (
      <div className="flex flex-col items-center px-6 pt-10 text-center">
        <div className="grid h-20 w-20 place-items-center rounded-full bg-primary-light text-primary">
          <Receipt size={32} />
        </div>
        <h2 className="mt-4 text-base font-semibold text-content-primary">Ready for your bill?</h2>
        <p className="mt-1 max-w-xs text-sm text-content-muted">
          Tap below to generate a consolidated invoice for everything you ordered.
        </p>
        {err && <div className="mt-3 text-sm text-danger">{err}</div>}
        <button
          type="button"
          onClick={() => void requestBill()}
          disabled={busy === 'request'}
          className="mt-6 inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        >
          {busy === 'request' ? 'Generating…' : 'Request Bill'}
        </button>
      </div>
    );
  }

  // BILL ready (or paid)
  return (
    <div className="bg-surface-card">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border">
        <div className="text-[11px] uppercase tracking-wider text-content-muted">Invoice</div>
        <h2 className="text-lg font-semibold text-content-primary">
          #{bill!.invoiceNumber ?? bill!.invoiceId}
        </h2>
        {paid && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-success-light px-2.5 py-1 text-[12px] font-medium text-success">
            <Check size={12} /> Paid
          </div>
        )}
      </div>

      {/* Lines */}
      <ul className="divide-y divide-border">
        {bill!.lines.map((l, i) => (
          <li key={i} className="flex items-center justify-between px-4 py-3 text-sm">
            <div>
              <div className="font-medium text-content-primary">{l.name}</div>
              <div className="text-[12px] text-content-muted">Qty {l.quantity} · ₹ {l.unitPrice.toFixed(2)}</div>
            </div>
            <div className="font-semibold text-content-primary">₹ {(l.unitPrice * l.quantity).toFixed(2)}</div>
          </li>
        ))}
      </ul>

      {/* Totals */}
      <div className="px-4 py-3 space-y-1 text-sm border-t border-border">
        <Row label="Subtotal" value={bill!.subtotal} />
        {bill!.taxAmount > 0 && <Row label="Tax" value={bill!.taxAmount} />}
        <Row label="Total" value={bill!.total} bold accent />
      </div>

      {/* Action buttons */}
      <div className="px-4 pb-6 pt-2 flex flex-col gap-2">
        {err && <div className="text-sm text-danger">{err}</div>}
        {!paid && (
          <button
            type="button"
            onClick={() => setShowPayModal(true)}
            disabled={busy === 'pay'}
            className="h-12 rounded-md bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
          >
            Pay ₹ {bill!.total.toFixed(2)}
          </button>
        )}
        {paid && customer?.phone && (
          <a
            href={whatsappInvoiceShareUrl(customer.phone, {
              invoiceNumber: bill!.invoiceNumber,
              total: bill!.total,
              tableCode: table?.tableCode,
            })}
            target="_blank"
            rel="noopener"
            className="h-12 grid place-items-center rounded-md bg-success text-base font-semibold text-white"
          >
            <span className="inline-flex items-center gap-2">
              <MessageCircle size={18} /> Share on WhatsApp
            </span>
          </a>
        )}
        {paid && (
          <div className="mt-3 rounded-md bg-success-light px-4 py-3 text-center text-[13px] text-success">
            Thank you for dining with us! ✨
          </div>
        )}
      </div>

      {showPayModal && !paid && (
        <PayModal
          total={bill!.total}
          busy={busy === 'pay'}
          onPay={(m) => void pay(m)}
          onClose={() => setShowPayModal(false)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */

function PayModal({
  total, busy, onPay, onClose,
}: {
  total: number;
  busy: boolean;
  onPay: (method: 'UPI' | 'Cash') => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
      {/* Cap the bottom sheet at 90vh so the payment options stay
          reachable on landscape phones / small laptops with the
          keyboard open. */}
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-surface-card p-4 shadow-2xl">
        <div className="mb-4 text-center">
          <div className="text-[11px] uppercase tracking-wider text-content-muted">Amount Due</div>
          <div className="text-2xl font-bold text-content-primary">₹ {total.toFixed(2)}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onPay('UPI')}
            disabled={busy}
            className="flex flex-col items-center gap-2 rounded-lg border-2 border-primary bg-primary-light px-4 py-4 text-primary disabled:opacity-60"
          >
            <IndianRupee size={28} />
            <span className="text-sm font-semibold">Pay UPI</span>
          </button>
          <button
            type="button"
            onClick={() => onPay('Cash')}
            disabled={busy}
            className="flex flex-col items-center gap-2 rounded-lg border-2 border-border bg-surface-app px-4 py-4 text-content-secondary disabled:opacity-60"
          >
            <Wallet size={28} />
            <span className="text-sm font-semibold">Pay Cash</span>
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="mt-4 h-11 w-full rounded-md border border-border text-sm text-content-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Empty({ title, subtitle, ctaTo }: { title: string; subtitle: string; ctaTo: string }) {
  return (
    <div className="flex flex-col items-center px-6 pt-12 text-center">
      <div className="grid h-24 w-24 place-items-center rounded-full bg-primary-light text-primary">
        <Receipt size={36} />
      </div>
      <h2 className="mt-4 text-base font-semibold text-content-primary">{title}</h2>
      <p className="mt-1 max-w-xs text-sm text-content-muted">{subtitle}</p>
      <Link
        to={ctaTo}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground"
      >
        Start Ordering
      </Link>
    </div>
  );
}

function Row({ label, value, bold, accent }: { label: string; value: number; bold?: boolean; accent?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${bold ? 'font-semibold' : ''} ${accent ? 'text-primary' : 'text-content-secondary'}`}>
      <span>{label}</span>
      <span>₹ {value.toFixed(2)}</span>
    </div>
  );
}
